/* eslint-disable react-refresh/only-export-components */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@be-miniapp/ui/lib/utils"

const titleVariant = cva("text-be tracking-normal", {
  variants: {
    variant: {
      bold: "font-bold",
      semi: "font-semibold",
    },
    size: {
      big: "text-xl",
      medium: "text-base",
      small: "text-sm",
    },
  },
  defaultVariants: {
    variant: "bold",
    size: "big",
  },
})

export interface TitleProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof titleVariant> {}

const Title = React.forwardRef<HTMLSpanElement, TitleProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <span
        className={cn(titleVariant({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)

Title.displayName = "Title"

const bodyVariant = cva("text-be tracking-normal", {
  variants: {
    variant: {
      bold: "font-bold",
      semi: "font-semibold",
      regular: "font-normal",
    },
    size: {
      big: "text-base",
      medium: "text-sm",
      small: "text-xs",
    },
  },
  defaultVariants: {
    variant: "regular",
    size: "big",
  },
})

export interface BodyProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof bodyVariant> {}

const Body = React.forwardRef<HTMLSpanElement, BodyProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <span
        className={cn(bodyVariant({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)

Body.displayName = "Body"

const labelVariant = cva("text-be tracking-normal", {
  variants: {
    variant: {
      default: "font-semibold",
    },
    size: {
      big: "text-base",
      medium: "text-sm",
      small: "text-xs",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "big",
  },
})

export interface LabelProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof labelVariant> {}

const Label = React.forwardRef<HTMLSpanElement, LabelProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <span
        className={cn(labelVariant({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)

Label.displayName = "Label"

const captionVariant = cva("text-be tracking-normal", {
  variants: {
    variant: {
      regular: "font-normal",
      semi: "font-semibold",
    },
    size: {
      big: "text-xs",
      medium: "text-[0.7rem] leading-[14px]",
      small: "text-[0.64rem] leading-[12px]",
    },
  },
  defaultVariants: {
    variant: "regular",
    size: "big",
  },
})

export interface CaptionProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof captionVariant> {}

const Caption = React.forwardRef<HTMLSpanElement, CaptionProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <span
        className={cn(captionVariant({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)

Label.displayName = "Label"

const Typography = {} as {
  Title: typeof Title
  Body: typeof Body
  Label: typeof Label
  Caption: typeof Caption
}

Typography.Title = Title
Typography.Body = Body
Typography.Label = Label
Typography.Caption = Caption

export { Typography }
